'use client';

import * as React from 'react';
import { Slider as BaseSlider } from '@base-ui/react/slider';
import styles from './Slider.module.css';
import clsx from 'clsx';

export interface SliderProps {
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  label?: string;
  showValue?: boolean;
  formatValue?: (value: number) => string;
  /**
   * Render a tick for every stop. Only meaningful on a discrete scale — keep
   * the stop count low (roughly 10 or fewer) or the ticks turn into noise.
   */
  marks?: boolean;
  /**
   * Accessible name for the underlying range input. Defaults to `label`.
   * Set this when the visible label alone would not make sense out of context.
   */
  ariaLabel?: string;
  /**
   * Human-readable value for screen readers, e.g. "Professional" instead of
   * "4". Strongly recommended whenever the number is a stand-in for a word.
   */
  getAriaValueText?: (value: number) => string;
  className?: string;
}

export const Slider = React.forwardRef<HTMLDivElement, SliderProps>(
  (
    {
      value,
      defaultValue = 50,
      onChange,
      min = 0,
      max = 100,
      step = 1,
      disabled = false,
      label,
      showValue = false,
      formatValue = (v) => String(v),
      marks = false,
      ariaLabel,
      getAriaValueText,
      className,
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const currentValue = value ?? internalValue;

    const handleValueChange = (newValue: number | number[]) => {
      const val = Array.isArray(newValue) ? newValue[0] : newValue;
      setInternalValue(val);
      onChange?.(val);
    };

    // One tick per stop, positioned as a percentage along the track.
    const stops = React.useMemo(() => {
      if (!marks || step <= 0) return [];

      const count = Math.floor((max - min) / step) + 1;

      return Array.from({ length: count }, (_, index) => {
        const stopValue = min + index * step;
        return {
          value: stopValue,
          percent: count === 1 ? 0 : ((stopValue - min) / (max - min)) * 100,
        };
      });
    }, [marks, min, max, step]);

    return (
      <div className={clsx(styles.container, className)}>
        {(label || showValue) && (
          <div className={styles.header}>
            {label && <span className={styles.label}>{label}</span>}
            {showValue && (
              <span className={styles.value}>{formatValue(currentValue)}</span>
            )}
          </div>
        )}
        <BaseSlider.Root
          ref={ref}
          value={currentValue}
          defaultValue={defaultValue}
          onValueChange={handleValueChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          className={styles.slider}
        >
          <BaseSlider.Control className={styles.control}>
            <BaseSlider.Track className={styles.track}>
              <BaseSlider.Indicator className={styles.indicator} />
              {stops.map((stop) => (
                <span
                  key={stop.value}
                  aria-hidden="true"
                  className={clsx(
                    styles.mark,
                    stop.value <= currentValue && styles.markFilled
                  )}
                  style={{ left: `${stop.percent}%` }}
                />
              ))}
              <BaseSlider.Thumb
                className={styles.thumb}
                getAriaLabel={
                  ariaLabel || label ? () => ariaLabel ?? label ?? '' : undefined
                }
                getAriaValueText={
                  getAriaValueText
                    ? (_formatted, thumbValue) => getAriaValueText(thumbValue)
                    : undefined
                }
              />
            </BaseSlider.Track>
          </BaseSlider.Control>
        </BaseSlider.Root>
      </div>
    );
  }
);

Slider.displayName = 'Slider';
